import type { Question } from '@/types';
import type { Answers } from '@/redux/slider';
import Converter from '@/utils/converter';

export const roundToDecimals = (number: number): number => {
  const flooredNumber = Math.floor(number);
  const stringedNumber = flooredNumber.toString();
  const lastNumber = parseInt(stringedNumber[stringedNumber.length - 1]);
  const roundEqualizer = lastNumber >= 5 || lastNumber === 0 ? 0 : 5;
  return Math.round((flooredNumber + roundEqualizer) / 10) * 10;
};

export const getFormattedMoney = (amount: number): string => {
  const money = Math.floor(amount).toString();
  const reversedMoney = money.split('').reverse();
  return reversedMoney
    .map((i, index) => {
      if ((index + 1) % 3 === 0 && reversedMoney.length > 3 && index < reversedMoney.length - 1) {
        return '.' + i;
      } else {
        return i;
      }
    })
    .reverse()
    .join('');
};

// `monthRate` is the displayed (rounded) monthly rate - the total-to-pay/savings shown to the
// customer must be consistent with the rate we tell them they're paying, fee included.
// `roundedMonths` must be an already whole-number term (Math.round, not ceil, to match Excel).
export const getPotentialSaving = (income: number, monthRate: number, roundedMonths: number): number => {
  const potentialSaving = Math.floor(income) - Math.floor(monthRate) * roundedMonths;
  return Number.isNaN(potentialSaving) || potentialSaving < 0 ? 0 : potentialSaving;
};

export const getPaidDebt = (monthRate: number, roundedMonths: number): number => {
  return Math.floor(monthRate) * roundedMonths;
};

export const formatFieldNumberValues = (string: string): string => {
  let normalized = string
    .replace(/[^\d,.]/g, '') // Remove all characters except 0-9, comma, and dot
    .replace(/[.]/g, ',')
    .replace(/[']/g, ',')
    .replace(/["]/g, ',')
    .replace(/[\s]/g, ',');

  const allowedSymbolsRegexp = new RegExp(/^[0-9,']*$/); // Allow commas instead of dots
  if (allowedSymbolsRegexp.test(normalized)) {
    const split = normalized.split(',');
    const splitLength = split.length;

    if (splitLength > 1) {
      const lastChunk = split.pop() || '';
      let decimalPart = lastChunk.slice(0, 2); // Take only the first two decimal digits
      decimalPart = decimalPart.padEnd(2, '0'); // Pad with 0s if there's only one decimal digit

      let integerPart = split.join('').replace(/[,]/g, '');
      if (integerPart.length > 3) {
        // If the integer part has dots, remove them and concatenate the rest
        integerPart = integerPart.replace(/[.]/g, '') + lastChunk.slice(2);
      } else {
        // Otherwise, simply concatenate the lastChunk to the integer part
        integerPart = integerPart + lastChunk;
      }

      return integerPart + ',' + decimalPart;
    } else {
      return `${normalized.replace(/^0+/, '')},00`; // Remove leading zeros if it's a whole number
    }
  } else {
    return '0,00';
  }
};

// TODO: Think about more general function
export const formatSubmitDataFieldNumberValues = (answers: Answers, questions: Question[]): Answers => {
  return Object.keys(answers).reduce((obj, answerKey) => {
    const questionIndex = questions.findIndex((question) => question.key === answerKey);
    if (questionIndex >= 0) {
      const { doFormatPrice, type } = questions[questionIndex];
      if (type === 'datefield') {
        Object.assign(obj, { [answerKey]: Converter.DateSting.ToDateStringUSFormat(answers[answerKey]) });
      }
      if (doFormatPrice) {
        Object.assign(obj, { [answerKey]: formatFieldNumberValues(answers[answerKey]) });
      } else {
        Object.assign(obj, { [answerKey]: answers[answerKey] });
      }
    } else {
      Object.assign(obj, { [answerKey]: answers[answerKey] });
    }
    return obj;
  }, {});
};

export const getNumberFromInput = (input: string) => {
  if (!input) {
    return 0;
  }
  const matchDigits = formatFieldNumberValues(input);
  return parseInt(matchDigits) || 0;
};
